import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Row,
  Col,
  Form,
  Table,
  Button,
  InputGroup,
  FormControl,
  Badge,
  Spinner,
  Pagination,
  Card,
  Alert,
} from "react-bootstrap";
import useFetch from "hooks/useFetch";
import "./AddPathSubscriptionsToManyUsers.scss";

const AddPathSubscriptionsToManyUsers = () => {
  const { t } = useTranslation("admin");
  const { get, post, isLoading } = useFetch();

  const getRef = useRef(get);
  useEffect(() => {
    getRef.current = get;
  }, [get]);

  const [learningPaths, setLearningPaths] = useState([]);
  const [selectedLearningPathId, setSelectedLearningPathId] = useState("");

  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [page, setPage] = useState(1);
  const perPage = 20;

  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successCount, setSuccessCount] = useState(null);

  const selectAllCheckboxRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const getLearningPaths = async () => {
    const res = await getRef.current("/admin/learning_paths");
    if (Array.isArray(res)) {
      setLearningPaths(res.map((lp) => ({ id: lp.id, title: lp.title })));
    }
  };

  const getUsersPage = async (q, p, pp) => {
    const url = `/admin/users?for_bulk_enrollment=true&include_subscriptions=false&query=${encodeURIComponent(
      q
    )}&page=${p}&per_page=${pp}`;
    const res = await getRef.current(url);
    if (res && Array.isArray(res.items)) {
      setUsers(res.items);
      setTotalUsers(typeof res.total === "number" ? res.total : res.items.length);
    } else {
      setUsers([]);
      setTotalUsers(0);
    }
  };

  useEffect(() => {
    getLearningPaths();
  }, []);

  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedQuery(query.trim());
      setPage(1);
    }, 300);
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [query]);

  useEffect(() => {
    getUsersPage(debouncedQuery, page, perPage);
  }, [debouncedQuery, page]);

  const allVisibleChecked = useMemo(
    () => users.length > 0 && users.every((u) => selectedUserIds.has(u.id)),
    [users, selectedUserIds]
  );

  const someVisibleChecked = useMemo(
    () => users.some((u) => selectedUserIds.has(u.id)) && !allVisibleChecked,
    [users, selectedUserIds, allVisibleChecked]
  );

  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = someVisibleChecked;
    }
  }, [someVisibleChecked]);

  const toggleAllVisible = () => {
    const next = new Set(selectedUserIds);
    if (allVisibleChecked) {
      users.forEach((u) => next.delete(u.id));
    } else {
      users.forEach((u) => next.add(u.id));
    }
    setSelectedUserIds(next);
  };

  const toggleOne = (id) => {
    const next = new Set(selectedUserIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedUserIds(next);
  };

  const clearSelection = () => setSelectedUserIds(new Set());

  const handleEnroll = async () => {
    setErrorMessage("");
    setSuccessCount(null);
    if (!selectedLearningPathId) {
      setErrorMessage(t("select_learning_path"));
      return;
    }
    if (selectedUserIds.size === 0) {
      setErrorMessage(t("select_at_least_one_user"));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        learning_path_id: selectedLearningPathId,
        user_ids: Array.from(selectedUserIds),
      };
      const res = await post("/admin/subscriptions?for_bulk_enrollment=true", payload);
      if (res && typeof res.enrolled_count === "number") {
        setSuccessCount(res.enrolled_count);
        clearSelection();
      } else {
        setErrorMessage(t("unexpected_response"));
      }
    } catch (e) {
      setErrorMessage(e?.message || t("server_error"));
    } finally {
      setSubmitting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalUsers / perPage));

  return (
    <div className="AddPathSubscriptionsToManyUsers">
      <Card className="mb-3 border-1 shadow-sm mt-3">
        <Card.Body>
          <Row className="align-items-end">
            <Col md={4} className="mb-3">
              <Form.Label className="mb-1 font-weight-bold">
                {t("learning_path")}
              </Form.Label>
              <Form.Control
                as="select"
                value={selectedLearningPathId}
                onChange={(e) => setSelectedLearningPathId(e.target.value)}
                custom
              >
                <option value="" disabled>
                  {learningPaths.length ? t("choose_one") : t("none_available")}
                </option>
                {learningPaths.map((learningPath) => (
                  <option key={learningPath.id} value={learningPath.id}>
                    {learningPath.title}
                  </option>
                ))}
              </Form.Control>
            </Col>
            <Col md={5} className="mb-3">
              <Form.Label className="mb-1 font-weight-bold">
                {t("search_users")}
              </Form.Label>
              <InputGroup>
                <FormControl
                  placeholder={t("search_by_name")}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <InputGroup.Append>
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      setQuery("");
                      setPage(1);
                    }}
                  >
                    {t("clear")}
                  </Button>
                </InputGroup.Append>
              </InputGroup>
            </Col>
            <Col md={3} className="mb-3 text-md-right">
              <div className="mb-2">
                <Badge variant="warning" className="mr-2">
                  {t("selected")}: {selectedUserIds.size}
                </Badge>
                {successCount !== null && (
                  <Badge variant="success" className="mr-2">
                    {t("enrolled")}: {successCount}
                  </Badge>
                )}
              </div>
              <button
                className="enroll-button mr-2"
                onClick={handleEnroll}
                disabled={submitting || isLoading}
              >
                {submitting ? t("processing") : t("enroll")}
              </button>
            </Col>
          </Row>
          {errorMessage && (
            <Alert
              className="mt-2 mb-0"
              variant="danger"
              onClose={() => setErrorMessage("")}
              dismissible
            >
              {errorMessage}
            </Alert>
          )}
        </Card.Body>
      </Card>

      <Card className="main-bulk-subscriptions-card mt-3">
        <Card.Body className="p-0 bulk-subscriptions-card border-1">
          <Table striped hover bordered className="mb-0">
            <thead>
              <tr>
                <th style={{ width: 44 }}>
                  <Form.Check
                    type="checkbox"
                    custom
                    id="select-all-visible"
                    checked={allVisibleChecked}
                    onChange={toggleAllVisible}
                    ref={selectAllCheckboxRef}
                  />
                </th>
                <th>{t("first_name")}</th>
                <th>{t("last_name")}</th>
                <th>{t("email")}</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={4} className="text-center py-4">
                    <Spinner animation="border" role="status" />
                  </td>
                </tr>
              )}
              {!isLoading && users.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center text-muted py-4">
                    {t("no_users_found")}
                  </td>
                </tr>
              )}
              {!isLoading &&
                users.map((user) => (
                  <tr
                    key={user.id}
                    className={selectedUserIds.has(user.id) ? "selected-row" : ""}
                  >
                    <td>
                      <Form.Check
                        type="checkbox"
                        custom
                        id={`pick-${user.id}`}
                        checked={selectedUserIds.has(user.id)}
                        onChange={() => toggleOne(user.id)}
                      />
                    </td>
                    <td>{user.first_name}</td>
                    <td>{user.last_name}</td>
                    <td className="text-monospace">{user.email}</td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </Card.Body>
        {totalPages > 1 && (
          <Card.Footer className="bg-white mt-3 border-0 mb-3">
            <Row className="justify-content-center">
              <Pagination className="mb-0">
                <Pagination.Prev
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                />
                {Array.from({ length: totalPages }).map((_, i) => (
                  <Pagination.Item
                    key={i + 1}
                    active={page === i + 1}
                    onClick={() => setPage(i + 1)}
                  >
                    {i + 1}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                />
              </Pagination>
            </Row>
          </Card.Footer>
        )}
      </Card>
    </div>
  );
};

export default AddPathSubscriptionsToManyUsers;
